// main.js — Entry point; wires all modules

import { initTheme }    from './theme.js';
import { initThemeRipple } from './themeRipple.js';
import { initCanvas }   from './canvas.js';
import { initHeaderPulse } from './headerPulse.js';
import { initParallax } from './parallax.js';
import { initReveal }   from './reveal.js';
import { initCursor }   from './cursor.js';
import { scramble }     from './scramble.js';
import { initGithub }   from './github.js';
import { initWeather }  from './weather.js';

function forceScrollTop() {
  const root = document.documentElement;
  const prevBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  // Double-set on next frame to beat browser restoration race conditions.
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
    root.style.scrollBehavior = prevBehavior;
  });
}

function lockTextFootprint(el) {
  const finalText = el.dataset.final || el.textContent.trim();
  if (!finalText) return;

  const probe = document.createElement('span');
  const cs = window.getComputedStyle(el);
  probe.textContent = finalText;
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.whiteSpace = 'pre';
  probe.style.fontFamily = cs.fontFamily;
  probe.style.fontSize = cs.fontSize;
  probe.style.fontWeight = cs.fontWeight;
  probe.style.letterSpacing = cs.letterSpacing;
  probe.style.lineHeight = cs.lineHeight;
  document.body.appendChild(probe);

  const { width } = probe.getBoundingClientRect();
  probe.remove();

  const parentWidth = el.parentElement?.clientWidth ?? window.innerWidth;
  const lockedWidth = Math.min(Math.ceil(width), Math.max(0, Math.floor(parentWidth)));

  el.style.display = 'inline-block';
  el.style.width = `${lockedWidth}px`;
  el.style.maxWidth = '100%';
  el.style.whiteSpace = 'nowrap';
  el.style.textAlign = 'center';
}

function createHeroScrambleOverlay(baseEl) {
  const headline = baseEl.closest('.hero-headline');
  if (!headline) return null;

  const computedHeadline = window.getComputedStyle(headline);
  if (computedHeadline.position === 'static') {
    headline.style.position = 'relative';
  }

  const computedBase = window.getComputedStyle(baseEl);
  const overlay = document.createElement('span');
  overlay.dataset.heroScrambleOverlay = 'true';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.textContent = baseEl.dataset.final || baseEl.textContent.trim();

  Object.assign(overlay.style, {
    position: 'absolute',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    fontFamily: computedBase.fontFamily,
    fontSize: computedBase.fontSize,
    fontWeight: computedBase.fontWeight,
    fontStyle: computedBase.fontStyle,
    lineHeight: computedBase.lineHeight,
    letterSpacing: computedBase.letterSpacing,
    color: computedBase.color,
  });

  headline.appendChild(overlay);
  return overlay;
}

function measureTextWidth(el, text) {
  const probe = document.createElement('span');
  const cs = window.getComputedStyle(el);
  probe.textContent = text;
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.whiteSpace = 'pre';
  probe.style.fontFamily = cs.fontFamily;
  probe.style.fontSize = cs.fontSize;
  probe.style.fontWeight = cs.fontWeight;
  probe.style.fontStyle = cs.fontStyle;
  probe.style.letterSpacing = cs.letterSpacing;
  probe.style.lineHeight = cs.lineHeight;
  document.body.appendChild(probe);

  const { width } = probe.getBoundingClientRect();
  probe.remove();
  return width;
}

function measureHeroSubWordWidths(word) {
  if (!word) return null;
  const finalWord = word.dataset.final?.trim() || word.textContent.trim();
  const altWord = word.dataset.alt?.trim();
  if (!finalWord || !altWord) return null;

  const finalWordWidth = Math.ceil(measureTextWidth(word, finalWord));
  const altWordWidth = Math.ceil(measureTextWidth(word, altWord));
  return { finalWordWidth, altWordWidth };
}

function initHeroSubEdit() {
  const word = document.querySelector('.hero-edit-word');
  const phrase = document.querySelector('.hero-sub-phrase');
  if (!word || !phrase) return;

  const initialDelayMs = 3000;
  const swapDurationMs = 260;

  const finalWord = word.dataset.final?.trim() || word.textContent.trim();
  const altWord = word.dataset.alt?.trim();
  if (!finalWord || !altWord) return;

  let hasStarted = false;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const showReduced = () => {
      const widths = measureHeroSubWordWidths(word);
      word.textContent = finalWord;
      if (widths) word.style.width = `${widths.finalWordWidth}px`;
      phrase.classList.add('is-ready');
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(showReduced);
    } else {
      showReduced();
    }
    return;
  }

  const syncHeroSubWordLayout = () => {
    const widths = measureHeroSubWordWidths(word);
    if (!widths) return;
    word.style.width = word.textContent.trim() === finalWord
      ? `${widths.finalWordWidth}px`
      : `${widths.altWordWidth}px`;
  };

  const start = () => {
    if (hasStarted) return;
    hasStarted = true;

    word.textContent = altWord;
    const widths = measureHeroSubWordWidths(word);
    if (!widths) return;
    const { finalWordWidth, altWordWidth } = widths;
    word.style.width = `${altWordWidth}px`;
    phrase.classList.add('is-ready');

    window.setTimeout(() => {
      requestAnimationFrame(() => {
        word.classList.add('is-swapping');
      });

      window.setTimeout(() => {
        word.textContent = finalWord;
        word.style.width = `${finalWordWidth}px`;

        requestAnimationFrame(() => {
          word.classList.remove('is-swapping');
        });
      }, swapDurationMs);
    }, initialDelayMs);
  };

  let rafId = null;
  const relayoutPhrase = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      syncHeroSubWordLayout();
    });
  };

  window.addEventListener('resize', relayoutPhrase, { passive: true });
  window.addEventListener('orientationchange', relayoutPhrase, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      relayoutPhrase();
      start();
    });
  } else {
    relayoutPhrase();
    start();
  }
}

function initClock() {
  const el = document.getElementById('ca-clock');
  if (!el) return;
  const hideClockMq = window.matchMedia('(max-width: 374px)');

  const syncClockVisibility = () => {
    const shouldHide = hideClockMq.matches;
    el.hidden = shouldHide;
    el.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
    el.style.display = shouldHide ? 'none' : '';
  };

  function tick() {
    const now = new Date();
    // California = America/Los_Angeles (handles PST/PDT automatically)
    const time = now.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    // Append PST or PDT depending on daylight saving time
    const tz = now.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short',
    }).split(', ')[1] ?? 'PT';
    el.textContent = `${time} ${tz}`;
    el.setAttribute('datetime', now.toISOString());
  }

  syncClockVisibility();
  hideClockMq.addEventListener('change', syncClockVisibility);
  tick();
  setInterval(tick, 1000);
}

function initFooterQuotes() {
  const el = document.getElementById('footer-quote');
  if (!el) return;

  const quotes = [
    ['wealth is in the mind,', 'not the pocket'],
    ['Practice the art of interpreting', 'situations constructively'],
    ['The dog that walks around', 'will find the stick'],
    ['Too many accomplishments', 'make no accomplishment'],
    ['Dumplings are', 'better than flowers'],
    ['All you need is less'],
  ];

  const renderQuote = ([firstLine, secondLine]) => {
    el.replaceChildren();
    el.append(document.createTextNode(firstLine));

    if (!secondLine) return;

    const breakEl = document.createElement('span');
    breakEl.className = 'footer-mobile-break';
    breakEl.setAttribute('aria-hidden', 'true');
    el.append(breakEl, document.createTextNode(secondLine));
  };

  const randomIndex = Math.floor(Math.random() * quotes.length);
  renderQuote(quotes[randomIndex]);
}

export function initHeroHeadlineScramble(heroTarget = document.querySelector('.hero-content .scramble-target')) {
  if (!heroTarget) return;

  const finalText = heroTarget.dataset.final || heroTarget.textContent.trim();
  if (!finalText) return;

  let rafId = null;

  const relockHero = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => lockTextFootprint(heroTarget));
  };

  const startHero = () => {
    heroTarget.textContent = finalText;
    lockTextFootprint(heroTarget);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      heroTarget.style.removeProperty('color');
      return;
    }

    const overlay = createHeroScrambleOverlay(heroTarget);
    if (!overlay) return;

    const previousInlineColor = heroTarget.style.color;
    heroTarget.style.color = 'transparent';

    scramble(overlay, {
      duration: 1100,
      delay: 350,
      onComplete: () => {
        overlay.remove();
        if (previousInlineColor) {
          heroTarget.style.color = previousInlineColor;
        } else {
          heroTarget.style.removeProperty('color');
        }
      },
    });

    window.addEventListener('resize', relockHero, { passive: true });
    window.addEventListener('orientationchange', relockHero, { passive: true });
  };

  if (document.fonts?.ready) {
    document.fonts.ready.then(startHero);
  } else {
    startHero();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  forceScrollTop();

  initClock();
  // Theme must be first (prevents flash)
  initTheme();
  initThemeRipple();
  initHeaderPulse();

  // Canvas particle background
  initCanvas();

  // Parallax depth layers
  initParallax();

  // Scroll-reveal + card scrambles
  initReveal();

  // Custom cursor + card tilt (non-touch only)
  if (window.matchMedia('(pointer: fine)').matches) {
    initCursor();
  }

  // GitHub 14-day commit strip (non-blocking, no token needed)
  initGithub();

  // SF 94110 live temperature
  initWeather();

  // Hero subline self-correction joke
  initHeroSubEdit();

  // Footer quote rotation with mobile-specific line breaks
  initFooterQuotes();

  // Hero headline scramble on load
  initHeroHeadlineScramble();
});

window.addEventListener('pageshow', () => {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  forceScrollTop();
});

window.addEventListener('load', forceScrollTop, { once: true });
